import React, { useState, useRef, useEffect } from 'react';

const LazyVideo = ({ 
  videoSrc, 
  posterSrc, 
  posterWebpSrc,
  alt = "Video content",
  className = '',
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  threshold = 0.1,
  rootMargin = '100px'
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const videoRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          // Delay video loading slightly to prioritize other content
          setTimeout(() => setShowVideo(true), 500);
        }
      },
      { 
        threshold,
        rootMargin
      }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => observer.disconnect();
  }, [isInView, threshold, rootMargin]);

  const handleVideoLoad = () => {
    setIsLoaded(true);
  };

  return (
    <div 
      ref={videoRef} 
      className={`lazy-video-container ${className}`}
    >
      {showVideo ? (
        <video
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          poster={posterWebpSrc || posterSrc}
          className={`lazy-video ${isLoaded ? 'loaded' : 'loading'}`}
          onLoadedData={handleVideoLoad}
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      ) : isInView ? (
        // Show poster image while video loads
        <picture className="lazy-poster">
          {posterWebpSrc && <source srcSet={posterWebpSrc} type="image/webp" />}
          <img 
            src={posterSrc} 
            alt={alt}
            className="lazy-poster-image"
          />
        </picture>
      ) : (
        // Placeholder before coming into view
        <div className="lazy-placeholder">
          Loading video...
        </div>
      )}
    </div>
  );
};

export default LazyVideo; 